import { useMemo, useState } from "react"
import {
	IconCheckOutline18 as Check,
	IconXmarkOutline18 as XIcon,
} from "nucleo-ui-outline-18"

import type { EnumValue, FieldRule, TemplatedJson } from "@productlane/lib"
import { Badge, Button, Input, SegmentedControl } from "@productlane/ui"

import type { RefsState } from "../model/refs-state"
import type { SecureRefOption } from "../model/secure-ref-picker"
import type { JsonKind, SchemaField } from "../model/tool-schema-fields"
import { FieldNotice } from "../inputs/field-notice"
import { OptionSelect } from "../inputs/option-select"
import { ScalarTokenField } from "../inputs/scalar-token-field"
import { useCommittedDraft } from "../inputs/use-committed-draft"
import {
	agentTypesFor,
	defaultKindFor,
	editorKindFor,
	emptyRuleFor,
	isContainerKind,
	isValueCompatible,
	kindLabel,
	parseTypedScalar,
	recoerceFixedValue,
	storedKindOf,
} from "../model/typed-value"
import { JsonValueEditor } from "./json-value-editor"

type PinnedRule = Extract<FieldRule, { mode: "fixed" | "json" }>

interface Props {
	field: SchemaField
	rule: PinnedRule
	knownKeys?: readonly string[]
	refOptions: readonly SecureRefOption[]
	refsState: RefsState
	onRetryRefs: () => void
	onChange: (rule: FieldRule, save?: boolean) => void
}

const BOOLEAN_OPTIONS = [
	{ value: "true" as const, label: "True", Icon: Check },
	{ value: "false" as const, label: "False", Icon: XIcon },
]

/** Every kind the picker offers, so a union or an unconstrained field can pick one. */
function offeredKinds(field: SchemaField): JsonKind[] {
	const kinds =
		field.kinds.length > 0 ? [...field.kinds] : ["string" as JsonKind]
	if (field.nullable && !kinds.includes("null")) kinds.push("null")
	return kinds
}

export function FixedValueEditor({
	field,
	rule,
	knownKeys,
	refOptions,
	refsState,
	onRetryRefs,
	onChange,
}: Props) {
	const storedKind = storedKindOf(rule)
	const kinds = offeredKinds(field)
	// The stored kind wins so a value saved before the schema changed is shown as it
	// is, rather than flattened into whatever the schema now asks for. An override
	// only bridges the moment between picking a kind and the new rule arriving.
	const [kindOverride, setKindOverride] = useState<JsonKind | null>(null)
	if (kindOverride !== null && kindOverride === storedKind) {
		setKindOverride(null)
	}
	const kind = kindOverride ?? storedKind

	// A stored value that disagrees with the schema-derived editor still needs a
	// control, so the rule's own shape breaks the tie.
	const editor = (() => {
		const derived = editorKindFor(field, kind)
		if (derived === "scalarList" && rule.mode === "json") return "json"
		if (derived === "json" && rule.mode === "fixed") {
			return Array.isArray(rule.value)
				? "scalarList"
				: editorKindFor(field, storedKind)
		}
		return derived
	})()

	const mismatch =
		rule.mode === "fixed" && !isValueCompatible(rule.value, field)

	const repair = useMemo(() => {
		if (!mismatch || rule.mode !== "fixed") return null
		const wanted = defaultKindFor(field)
		const retyped = recoerceFixedValue(rule.value, wanted)
		return retyped.ok ? { wanted, value: retyped.value } : null
	}, [mismatch, rule, field])

	const jsonKinds = useMemo(() => (isContainerKind(kind) ? [kind] : []), [kind])

	const setScalar = (value: EnumValue | null, save?: boolean) =>
		onChange({ mode: "fixed", value }, save)

	const switchKind = (next: JsonKind) => {
		setKindOverride(next)
		onChange(emptyRuleFor(next, field))
	}

	return (
		<div className="mt-1.5 space-y-1.5">
			{kinds.length > 1 ? (
				<OptionSelect
					size="sm"
					value={kind}
					options={kinds.map((entry) => ({
						value: entry,
						label: kindLabel(entry),
					}))}
					onSelect={switchKind}
				/>
			) : null}

			{editor === "json" && rule.mode === "json" ? (
				<JsonValueEditor
					name={`json-${field.path}`}
					value={rule.value}
					kinds={jsonKinds}
					knownKeys={knownKeys}
					refOptions={refOptions}
					refsState={refsState}
					onRetryRefs={onRetryRefs}
					agentTypes={agentTypesFor(field)}
					onCommit={(value: TemplatedJson) => onChange({ mode: "json", value })}
				/>
			) : null}

			{editor === "boolean" && rule.mode === "fixed" ? (
				<SegmentedControl
					name={`bool-${field.path}`}
					value={rule.value === true ? "true" : "false"}
					options={BOOLEAN_OPTIONS}
					showLabels
					onValueChange={(next) => setScalar(next === "true")}
				/>
			) : null}

			{editor === "null" ? <Badge variant="dark-gray">null</Badge> : null}

			{editor === "enumSelect" && rule.mode === "fixed" ? (
				<OptionSelect
					size="sm"
					value={String(rule.value)}
					label={rule.value === "" ? "Pick a value" : String(rule.value)}
					options={(field.enumValues ?? []).map((value) => ({
						value: String(value),
						label: String(value),
					}))}
					onSelect={(next) =>
						setScalar(
							(field.enumValues ?? []).find(
								(value) => String(value) === next,
							) ?? next,
						)
					}
				/>
			) : null}

			{editor === "scalarList" && rule.mode === "fixed" ? (
				<ScalarTokenField
					name={`list-${field.path}`}
					values={Array.isArray(rule.value) ? rule.value : []}
					itemKinds={field.itemKinds}
					placeholder="Add an entry"
					onChange={(values) => onChange({ mode: "fixed", value: values })}
				/>
			) : null}

			{(editor === "text" || editor === "number") &&
			rule.mode === "fixed" &&
			!Array.isArray(rule.value) ? (
				<ScalarInput
					field={field}
					kind={kind}
					value={rule.value}
					onChange={setScalar}
				/>
			) : null}

			{mismatch ? (
				<FieldNotice
					action={
						repair ? (
							<Button
								variant="outline"
								size="xs"
								onClick={() => {
									setKindOverride(repair.wanted)
									onChange({ mode: "fixed", value: repair.value })
								}}
							>
								Use {JSON.stringify(repair.value)}
							</Button>
						) : null
					}
				>
					This tool wants {field.kinds.map(kindLabel).join(" or ")}. Saved as{" "}
					{kindLabel(storedKind)}.
				</FieldNotice>
			) : null}
		</div>
	)
}

function ScalarInput({
	field,
	kind,
	value,
	onChange,
}: {
	field: SchemaField
	kind: JsonKind
	value: EnumValue | null
	onChange: (value: EnumValue | null, save?: boolean) => void
}) {
	const numeric = kind === "number" || kind === "integer"

	const draft = useCommittedDraft<EnumValue | null>(value, {
		onCommit: onChange,
		parse: (raw) => parseTypedScalar(raw, kind),
	})

	return (
		<Input
			name={`fixed-${field.path}`}
			value={draft.value}
			textSize="sm"
			inputMode={numeric ? "decimal" : undefined}
			placeholder={numeric ? "0" : "eur"}
			errorMessage={draft.error ?? undefined}
			onChange={(event) => draft.setValue(event.target.value)}
			onBlur={draft.commit}
		/>
	)
}
